import React, { useCallback, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import _merge from "lodash/merge";
import { useSelector } from "react-redux";

import { CardForm, PaymentSystem } from "@web-solutions/react-billing";
import { t } from "@web-solutions/module-localization";

import Analytics from "@web-solutions/module-analytics";

import { remoteConfigSelector } from "core/store/remote-config/selectors";
import { selectPaymentSystem } from "core/store/billing/selectors";
import { useButtonTitleByConfig } from "core/utils/button-configurator";
import { ButtonPlacementType } from "core/payment/constants";

import "./style.scss";
import getLocale from "core/localization/get-locale";

const PaymentForm = ({
  product,
  onSubmit,
  onSuccess,
  onError,
  onApplePayClick,
  onApplePaySubmit,
  onGooglePayClick,
  onGooglePaySubmit,
  onUserInfoChange,
  options,
  formClassName,
  fieldClassName,
  inputStyles,
  labelStyles,
  notEmptyLabelStyles,
  isSimpleView,
  color,
  onSaveTokenOneTime,
}) => {
  const startEnterCardForm = useRef(false);
  const completedEnterCardForm = useRef(false);

  const paymentSystem = useSelector(selectPaymentSystem);
  const { additionalFields, buttonsConfigs, paymentPopup } =
    useSelector(remoteConfigSelector);
  const email = useSelector((state) => state.profile.email);
  const shippingAddressRef = useRef();
  const buttonConfig = buttonsConfigs[ButtonPlacementType.PAYMENT];
  const locale = getLocale();
  const buttonTitle = product?.buttonTitleModal?.[locale] || 'Start Now';
  const buttonClassNames = classNames("form-button", "form-button-title");

  const additionalField = useMemo(() => {
    const fields = additionalFields?.email
      ? _merge(additionalFields, { email: { value: email } })
      : additionalFields;

    // Stripe puts the name into `billing_details`; TailoredPay needs it for the
    // Gateway.js payer-authentication contact (firstName/lastName), so both get
    // the two extra inputs. Other providers collect the cardholder inside their
    // own hosted fields.
    const needsNameFields =
      paymentSystem === PaymentSystem.STRIPE ||
      paymentSystem === PaymentSystem.TAILOREDPAY;

    if (!needsNameFields) {
      return fields;
    }

    // TailoredPay also gets a ZIP: NMI uses the postal code for AVS, and it is
    // part of the cardholder data Gateway.js sends for payer authentication — so
    // filling it in improves both the decline rate and the 3DS outcome.
    // The placeholder is read defensively: the fetched remote config REPLACES the
    // built-in one instead of merging into it (and is persisted), so a key added
    // in code is simply absent until the mutator schema is republished.
    const zipField = paymentSystem === PaymentSystem.TAILOREDPAY
      ? {
        zip: {
          label: '',
          placeholder: paymentPopup?.cardElementRecurly?.zip?.[locale] || 'ZIP code',
          type: 'text',
        },
      }
      : {};

    return {
      ...fields,
      ...zipField,
      first_name: {
        label: '',
        placeholder: paymentPopup.cardElementRecurly.firstName[locale],
        type: 'text',
      },
      last_name: {
        label: '',
        placeholder: paymentPopup.cardElementRecurly.lastName[locale],
        type: 'text',
      },
    };
  }, [additionalFields, email, locale, paymentPopup, paymentSystem]);

  const handleButtonSubmit = async (formData) => {
    try {
      Analytics.trackEvent("submit_pay_button", "click");
      await onSubmit(formData);
    } catch (error) {
      throw error;
    }
  };

  const handleFormReady = useCallback(() => {
    Analytics.trackEvent("card_form", "ready");
  }, []);

  const handleFormChange = useCallback(
    (e) => {
      // TODO refactor target check
      if (!e.target || paymentSystem === PaymentSystem.SOLIDGATE) {
        if (!e.error) {
          if (!startEnterCardForm.current && !e.empty) {
            startEnterCardForm.current = true;
            Analytics.trackEvent("card_form_enter", "start");
          } else if (
            !completedEnterCardForm.current &&
            (e.complete || e.valid)
          ) {
            completedEnterCardForm.current = true;
            Analytics.trackEvent("card_form_enter", "completed", {
              brand: e.brand,
            });
          }
        }
      }
    },
    [paymentSystem]
  );

  const validateChildren = () => {
    return shippingAddressRef?.current?.validateFields();
  };

  return (
    <CardForm
      isSimpleView={isSimpleView}
      product={product}
      color={color}
      onReady={handleFormReady}
      onChange={handleFormChange}
      onSubmit={handleButtonSubmit}
      onSuccess={onSuccess}
      onError={onError}
      onApplePayClick={onApplePayClick}
      onApplePaySubmit={onApplePaySubmit}
      onGooglePayClick={onGooglePayClick}
      onGooglePaySubmit={onGooglePaySubmit}
      onUserInfoChange={onUserInfoChange}
      formClassName={formClassName}
      fieldClassName={fieldClassName}
      buttonClassName={buttonClassNames}
      buttonTitle={buttonTitle}
      additionalFields={additionalField}
      options={options}
      inputStyles={inputStyles}
      labelStyles={labelStyles}
      notEmptyLabelStyles={notEmptyLabelStyles}
      onValidateChildren={validateChildren}
      onSaveTokenOneTime={onSaveTokenOneTime}
    ></CardForm>
  );
};

PaymentForm.propTypes = {
  product: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  onApplePayClick: PropTypes.func.isRequired,
  onApplePaySubmit: PropTypes.func.isRequired,
  onGooglePayClick: PropTypes.func.isRequired,
  onGooglePaySubmit: PropTypes.func.isRequired,
  onUserInfoChange: PropTypes.func.isRequired,
  options: PropTypes.object,
  labelStyles: PropTypes.object,
  inputStyles: PropTypes.object,
  notEmptyLabelStyles: PropTypes.object,
};

PaymentForm.defaultProps = {
  options: null,
  inputStyles: null,
  labelStyles: null,
  notEmptyLabelStyles: null,
};

export default PaymentForm;
